import { Collapse, Typography } from '@mui/material';
import { mixinSx } from '@watershed/style/styleUtils';
import { ComponentProps, ReactNode, useId, useState } from 'react';
import CollapseIcon from './CollapseIcon';
import Button from './Button';
import Well from './Well';

export default function CollapsibleWell({
  header,
  children,
  initiallyExpanded = false,
  isExpanded: propIsExpanded,
  onExpanded,
  chevronOnly = false,
  component,
  sx,
}: ComponentProps<typeof Well> & {
  children: ReactNode;
  header: ComponentProps<typeof Well.Header>;
  initiallyExpanded?: boolean;
  isExpanded?: boolean;
  onExpanded?: (expanded: boolean) => void;
  chevronOnly?: boolean;
}) {
  const [stateIsExpanded, setStateIsExpanded] = useState(
    propIsExpanded ?? initiallyExpanded ?? true
  );
  const isExpanded = propIsExpanded ?? stateIsExpanded;
  const contentDomId = useId();
  const icon = <CollapseIcon color="text.secondary" />;
  return (
    <Well
      component={component}
      sx={mixinSx(
        {
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          maxHeight: isExpanded ? 'auto' : undefined,
        },
        sx
      )}
    >
      <Well.Header
        {...header}
        actions={
          <>
            {header.actions}
            <Button
              variant="text"
              aria-expanded={isExpanded}
              aria-controls={contentDomId}
              onClick={() => {
                const newExpanded = !isExpanded;
                setStateIsExpanded(newExpanded);
                onExpanded?.(newExpanded);
              }}
              endIcon={!chevronOnly && icon}
              isIcon={chevronOnly}
            >
              {chevronOnly ? (
                icon
              ) : (
                <Typography
                  component="span"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                  aria-hidden
                  children={isExpanded ? 'Collapse' : 'Expand'}
                />
              )}
            </Button>
          </>
        }
      />
      <Collapse in={isExpanded}>
        <Well.Content id={contentDomId}>{children}</Well.Content>
      </Collapse>
    </Well>
  );
}
